import { useEffect, useRef, useState, type RefObject } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { isServerRendered } from '@yoweb/utils/isServerRendered';
import { inject } from '@yoweb/utils/scripts';
import { URL_PLACES_API } from '@yoweb/utils/constants/urls';
import {
  formatGooglePlacesDescription,
  formatStreetFromAddress,
  formatCityFromAddress,
} from '@yoweb/utils/intl/address';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';

type AddressAutocompleteProps = {
  /* Container for the PlacesService to render attributions*/
  attributionsElement: RefObject<HTMLDivElement>;

  /* A callback that receives the address that was selected */
  onAddressSelect: (address: Address) => void;

  /* A callback that receives the street address changes */
  onAddressInputChanged: (street: string) => void;

  /* Initial value of the input */
  initialValue?: string;

  /* Should reset when initialValue changes */
  enableReinitialize?: boolean;

  /* The country to restrict the address picker to */
  countryRestriction?: string;

  /* An optional callback for handling errors */
  onError?: (message: string) => void;

  /* Debounce in ms for the call to the Google Places api */
  debounce?: number;

  /* value from privateRuntimeConfig.places.publicKey(); */
  placesApiKey: string;
};

type Place = {
  description: string;
  placeId: string;
  highlights: Highlight[];
};

export type Address = {
  street?: string;
  city?: string;
  state?: string;
  stateIsoCode?: string;
  country?: string;
  countryIsoCode?: string;
  postalCode?: string;
};

type Highlight = {
  text: string;
  shouldHighlight: boolean;
};

type AddressDetailsKeys = keyof typeof addressMapping;

type AddressAutocompleteHook = {
  value: string;
  updateValue: (value: string) => void;
  selectPlace: (placeId: string) => void;
  isLoading: boolean;
  places: Place[] | null;
  getPlaces: () => void;
  clearPlaces: (inputValue?: string) => void;
};
const createHighlightMap = (
  text: string,
  matchedSubstrings: google.maps.places.PredictionSubstring[],
): Highlight[] => {
  const highlights: Highlight[] = [];
  let prevOffset = 0;

  const pushHighlight = (offset: number, length?: number, shouldHighlight = false) => {
    highlights.push({
      text: text.substring(offset, length === undefined ? undefined : offset + length),
      shouldHighlight,
    });
  };

  matchedSubstrings.forEach(({ offset, length }) => {
    if (prevOffset !== offset) {
      pushHighlight(prevOffset, text.substring(0, offset).length - prevOffset);
    }
    pushHighlight(offset, length, true);
    prevOffset = offset + length;
  });
  pushHighlight(prevOffset);

  return highlights;
};

const formatPredictions = (
  { description, matched_substrings, place_id }: google.maps.places.AutocompletePrediction,
  lcLocale: string,
) => {
  description = formatGooglePlacesDescription(description, lcLocale);

  return {
    description,
    placeId: place_id,
    highlights: createHighlightMap(description, matched_substrings),
  };
};

const addressMapping = {
  streetNumber: 'street_number',
  ward: 'sublocality_level_1', // used as a part of the city address for ja-JP
  district: 'sublocality_level_2', // used as a part of the street address for ja-JP
  route: 'route',
  city: 'locality',
  subState: 'administrative_area_level_2', // better name for this? used as a part of the city address for ja-JP
  state: 'administrative_area_level_1',
  country: 'country',
  postalCode: 'postal_code',
};

const getAddressFromPlaceResult = (
  placeResult: google.maps.places.PlaceResult | null,
  lcLocale: string,
): Address => {
  const addressDetails: AddressDetails = {};
  Object.keys(addressMapping).forEach((key: string) => {
    const addressComponent = placeResult?.address_components?.find(({ types }) =>
      types.includes(addressMapping[key as AddressDetailsKeys]),
    );

    if (!addressComponent) {
      return;
    }

    const { long_name: longName, short_name: shortName } = addressComponent;
    addressDetails[key as AddressDetailsKeys] = { longName, shortName };
  });

  return {
    street: formatStreetFromAddress(addressDetails, lcLocale),
    city: formatCityFromAddress(addressDetails, lcLocale),
    state: addressDetails.state?.longName,
    stateIsoCode: addressDetails.state?.shortName,
    country: addressDetails.country?.longName,
    countryIsoCode: addressDetails.country?.shortName,
    postalCode: addressDetails.postalCode?.shortName,
  };
};

export const useAddressAutocomplete = ({
  attributionsElement,
  countryRestriction,
  debounce = 200,
  enableReinitialize = false,
  initialValue = '',
  onAddressInputChanged,
  onAddressSelect,
  onError = () => {},
  placesApiKey = '',
}: AddressAutocompleteProps): AddressAutocompleteHook => {
  const initialValueRef = useRef(initialValue);
  const [value, setValue] = useState(initialValue);
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { lcLocale, language, country, localeIsPostalCodeDominant } = useIntlMeta();

  const placesService = useRef<google.maps.places.PlacesService>();
  const autocompleteService = useRef<google.maps.places.AutocompleteService>();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const initAutocomplete = () => {
    if (isServerRendered) {
      return;
    }

    if (!window.google?.maps?.places) {
      onError('Google Places api not loaded');
      return;
    }

    if (!attributionsElement.current) {
      onError('Places attributions element not found');
      return;
    }

    placesService.current = new google.maps.places.PlacesService(attributionsElement.current);
    autocompleteService.current = new google.maps.places.AutocompleteService();
  };

  const getPlaces = useDebouncedCallback(() => {
    if (!autocompleteService.current) {
      initAutocomplete();
    }
    const service = autocompleteService.current;
    // some locales auto-complete addresses by searching for regions
    // by a postal code instead of searching for addresses by a street name
    const types = [localeIsPostalCodeDominant ? 'postal_code' : 'address'];
    if (!value || !service) {
      return;
    }

    const config = {
      input: value,
      types,
      language,
      componentRestrictions: {
        country: countryRestriction ?? country,
      },
    };

    void service.getPlacePredictions(config, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        onError('Could not query Google Places api');
        return;
      }

      setPlaces(predictions?.map((prediction) => formatPredictions(prediction, lcLocale)) ?? null);
    });
  }, debounce);

  const updateValue = (value: string) => {
    setValue(value);
    onAddressInputChanged(value);

    if (!value) {
      clearPlaces();
      return;
    }

    getPlaces();
  };

  const clearPlaces = (inputValue = '') => {
    setPlaces(null);
    setValue(inputValue);
  };

  const selectPlace = (placeId: string) => {
    if (!placesService.current) {
      return;
    }

    setIsLoading(true);
    setPlaces(null);

    placesService.current.getDetails(
      { placeId, language, fields: ['address_components'] },
      (place: google.maps.places.PlaceResult | null) => {
        const address = getAddressFromPlaceResult(place, lcLocale);
        const newValue = localeIsPostalCodeDominant ? address.postalCode : address.street;
        setValue(newValue ?? '');
        onAddressSelect(address);
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    const init = async () => {
      if (placesApiKey) {
        try {
          await inject(
            `${URL_PLACES_API}/js?v=3.54&key=${placesApiKey}&libraries=places&callback=Function.prototype`,
            'google-places-api',
          );
          initAutocomplete();
        } catch (err) {
          onError(`Google Place api could not be loaded: ${err}`);
        }
      }
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (enableReinitialize && initialValueRef.current !== initialValue) {
      setValue(initialValue);
    }
  }, [enableReinitialize, initialValue]);

  return {
    value,
    updateValue,
    selectPlace,
    isLoading,
    places,
    getPlaces,
    clearPlaces,
  };
};

export default useAddressAutocomplete;
